/**
 * Since shared Players Stats needs to use types, but we will be fetching with different API clients so
 * for the moment, we will use the types from portal/src/libs/apiClient
 */

import {
	PlayerSearchResponse,
	PlayerSearchResponsePlayer,
	PlayerSearchPresetColumnResponse,
	SelectedCountriesResponse,
	PlayerSearchPresetColumnUpsertRequest,
	PlayerSearchPresetFilterResponse,
	PlayerSearchRequest,
	Sort,
	playerNameListResponse200,
	playerClubListResponse200,
	getAllTeamsResponse200,
	ApiResponse,
} from "../../../portal/src/libs/apiClient";

import { discoverPlayerSearchPlayerSearchResponse200 } from "../../../discover/app/libs/api-client";

export type ApiPlayerSearchResponse = PlayerSearchResponse;
export type ApiPlayerSearchResponsePlayer = PlayerSearchResponsePlayer;
export type ApiPlayerSearchPresetColumnUpsertRequest = PlayerSearchPresetColumnUpsertRequest;
export type ApiPlayerSearchPresetColumnResponse = PlayerSearchPresetColumnResponse;
export type ApiCountriesDataResponse = SelectedCountriesResponse;

export type PlayersGridPromises = {
	playerSearchPromise: (searchParams: SearchFilter) => Promise<{ data: ApiPlayerSearchResponse }>;
	playerSearchPresetColumnPutPromise: (id: string, body: any) => Promise<any>;
	playerSearchPresetColumnDeletePromise: (id: string) => Promise<any>;
	playerSearchPresetFilterPutPromise: (id: string, body: any) => Promise<any>;
	playerSearchPresetFilterDeletePromise: (id: string) => Promise<any>;
	getPlayerSearchPresetColumnPromise: () => Promise<{ data: ApiPlayerSearchPresetColumnResponse }>;
	getPlayerSearchPresetFilterPromise: () => Promise<{ data: PlayerSearchPresetFilterResponse }>;

	// Discover specific promises
	discoverPlayerListPromise?: (searchValue: string) => Promise<{ data: discoverPlayerSearchPlayerSearchResponse200 }>;

	// Portal specific promises
	portalGetPlayerNameListPromise?: (searchValue: string) => Promise<ApiResponse<playerNameListResponse200>>;
	portalGetPlayerClubListPromise?: (searchValue: string) => Promise<ApiResponse<playerClubListResponse200>>;
	portalGetPlayerTeamListPromise?: (searchValue: string, clubId?: number) => Promise<ApiResponse<getAllTeamsResponse200>>;
	portalFavoritePlayerPromise?: (playerId: number) => Promise<any>;
	portalUnFavoritePlayerPromise?: (playerId: number) => Promise<any>;
};

/**
 * Source of truth for table columns. DataGrid will render columns based on these keys.
 */
export enum ColumnEnum {
	name = "name",
	clubName = "clubName",
	teamName = "teamName",
	teamAgeGroup = "teamAgeGroup",
	strongFoot = "strongFoot",
	position = "position",
	birthday = "birthday",
	height = "height",
	weight = "weight",
	nationality = "nationality",
	rating = "rating",
	minutesPlayed = "minutesPlayed",
	gamesCount = "gamesCount",
	goalsScored = "goalsScored",
	goalsOwn = "goalsOwn",
	assists = "assists",
	totalGoals = "totalGoals",
	totalAssists = "totalAssists",
	shots = "shots",
	shotsOnTarget = "shotsOnTarget",
	shotsInsidePa = "shotsInsidePa",
	shotsOutsidePa = "shotsOutsidePa",
	takeons = "takeons",
	tackles = "tackles",
	aerialDuels = "aerialDuels",
	groundDuels = "groundDuels",
	recoveries = "recoveries",
	passes = "passes",
	passesAccuracy = "passesAccuracy",
	keyPasses = "keyPasses",
	passesForward = "passesForward",
	crosses = "crosses",
	controlUnderPressure = "controlUnderPressure",
	goalsConceded = "goalsConceded",
	catches = "catches",
	punches = "punches",
	goalsKicks = "goalsKicks",
	aerialClearances = "aerialClearances",
	gender = "gender",
}

export type TQuintiles = Map<string, { position: string; percentile: number; value: number }[]>;

export enum UserRole {
	Recruiter = "ROLE_RECRUITER",
	Admin = "ROLE_ADMIN",
	ClubManager = "ROLE_CLUB_MANAGER",
	Coach = "ROLE_COACH",
	DiscoverCoach = "ROLE_DISCOVER_COACH",
	DiscoverParent = "ROLE_DISCOVER_PARENT",
	DiscoverPlayer = "ROLE_DISCOVER_PLAYER",
	DiscoverClubManager = "ROLE_DISCOVER_CLUB_MANAGER",
	DiscoverWorksForClub = "ROLE_DISCOVER_WORKS_FOR_CLUB",
}

export enum NonPartnerClubType {
	Flat = "nonpartner",
	Kebab = "non-partner",
	Camel = "nonPartner",
}

export enum PlayerTypes {
	Partner = "partner",
	NonPartner = NonPartnerClubType.Flat,
}

export enum PlayerPosition {
	ALL = "ALL",
	G = "G",
	DD = "DD",
	DC = "DC",
	DG = "DG",
	MDC = "MDC",
	MC = "MC",
	MOC = "MOC",
	MG = "MG",
	MD = "MD",
	ATT = "ATT",
	AG = "AG",
	AD = "AD",
}

export enum OrderByType {
	asc = "asc",
	desc = "desc",
}

export interface CommonCellType {
	success?: number;
	total: number;
	type: string;
	position: string;
}

export interface PositionCellType {
	position: string;
	positionCount: number;
}

export interface PlayerFilter {
	birthday?: { gte?: number | null; lte?: number | null };
	clubId?: number[];
	clubName?: string[];
	countryCodes?: string[];
	height?: { gte?: number | null; lte?: number | null };
	nationality?: string[];
	playedInNationalTeam?: number;
	playerName?: string | null;
	position?: string;
	strongFoot?: string;
	teamAgeGroup?: string[];
	teamId?: number[];
	teamLevel?: string[];
	weight?: { gte?: number | null; lte?: number | null };
}

export type SearchFilter = Omit<PlayerSearchRequest, "player"> & {
	player?: Partial<PlayerSearchRequest["player"]>;
	sort: Sort[];
};

export enum SelectedType {
	default = "default",
	temp = "temp",
	custom = "custom",
}

export enum SubscriptionTypes {
	FULL = "full",
	PARTIAL = "partial",
	NONE = "none",
}

export enum PlayersOfTheWeekFilters {
	DEFAULT = "rating",
}

export enum ClubType {
	Partner = "partner",
	NonPartner = NonPartnerClubType.Camel,
}

export enum Status {
	NOT_AVAILABLE = "N/A",
}

export enum TableSortableColumns {
	SORT_ALL_COLUMNS = "sortAllColumns",
}

export enum PlayerEnum {
	birthday = "birthday",
	clubName = "clubName",
	countryCodes = "countryCodes",
	height = "height",
	nationality = "nationality",
	playerName = "playerName",
	position = "position",
	strongFoot = "strongFoot",
	teamAgeGroup = "teamAgeGroup",
	teamLevel = "teamLevel",
	playerAgeCategory = "playerAgeCategory",
	weight = "weight",
	playedInNationalTeam = "playedInNationalTeam",
	gender = "gender",
}

export enum StrongFoot {
	LEFT = "L",
	RIGHT = "R",
}

export enum FilterKeys {
	MINUTES = "minutesPlayed",
	GOALS = "goalsScored",
	GOALS_OWN = "goalsOwn",
	GAMES = "gamesCount",
	TOTAL_GOALS = "totalGoals",
	TOTAL_ASSISTS = "totalAssists",
}

/** Range for a single stat filter: [from, to] in slider/UI units (percentiles or raw values). */
export type StatFilterRange = [number, number];

/** Per-stat ranges keyed by stat key (STATS_FILTERS). */
export type StatsFilterRanges = Record<string, StatFilterRange>;

export enum TermsOfUseVersion {
	V1 = "v1",
	V2 = "v2",
	V3 = "v3",
}

export enum Role {
	ROLE_RECRUITER = "Recruiter",
	ROLE_CLUB_MANAGER = "ClubManager",
	ROLE_COACH = "Coach",
}

export interface DatagridTableColumns {
	[name: string]: string;
}

interface ItemResponseExtended {
	success: { value: number };
	total: { value: number };
}
interface ItemResponseShort {
	value: number;
}

export interface CountryInfo {
	countryCode: string;
	countryLabel: string;
}

export interface PlayerData {
	aerialClearances: ItemResponseExtended;
	aerialDuels: ItemResponseExtended;
	assists: ItemResponseShort;
	totalGoals: number;
	totalAssists: number;
	birthday: string;
	catches: ItemResponseShort;
	clubName: string;
	teamName: string;
	controlUnderPressure: ItemResponseShort;
	countryCode: string;
	crosses: ItemResponseExtended;
	firstName: string;
	lastName: string;
	goalsConceded: ItemResponseShort;
	goalsKicks: ItemResponseExtended;
	goalsScored: number;
	goalsOwn: number;
	groundDuels: ItemResponseExtended;
	height: string;
	id: number;
	keyPasses: ItemResponseShort;
	minutesPlayed: number;
	nationality: CountryInfo | string | null;
	nationality2: CountryInfo | string | null;
	gamesCount: string;
	partnerFlag: boolean;
	passes: ItemResponseExtended;
	passesAccuracy: ItemResponseShort;
	passesForward: ItemResponseExtended;
	position: string;
	positionCount: number;
	punches: ItemResponseShort;
	rating: ItemResponseShort;
	recoveries: ItemResponseShort;
	shots: ItemResponseShort;
	shotsInsidePa: ItemResponseShort;
	shotsOnTarget: ItemResponseShort;
	shotsOutsidePa: ItemResponseShort;
	strongFoot: string;
	tackles: ItemResponseExtended;
	takeons: ItemResponseExtended;
	teamAgeGroup: string;
	teamLevel: string;
	weight: string;
	playedInNationalTeam: number;
}

export interface PlayerResponse extends PlayerSearchResponse {
	limit: number;
	page: number;
	pages: number;
	total: number;
}

export type SelectedColumnsState = {
	items: {
		name: string;
		selectedColumns?: string[];
	}[];
	currentSelected: string;
};

export type PlayerSearchPresetFilter = {
	id: string;
	name: string;
	minutesPlayed: { gte?: number | null | undefined; lte?: number | null | undefined };
	goalsScored: { gte?: number | null | undefined; lte?: number | null | undefined };
	assists: { gte?: number | null | undefined; lte?: number | null | undefined };
	shots: { gte?: number | null | undefined; lte?: number | null | undefined };
	shotsOnTarget: { gte?: number | null | undefined; lte?: number | null | undefined };
	shotsInsidePa: { gte?: number | null | undefined; lte?: number | null | undefined };
	shotsOutsidePa: { gte?: number | null | undefined; lte?: number | null | undefined };
	takeons: { gte?: number | null | undefined; lte?: number | null | undefined };
	tackles: { gte?: number | null | undefined; lte?: number | null | undefined };
	aerialDuels: { gte?: number | null | undefined; lte?: number | null | undefined };
	groundDuels: { gte?: number | null | undefined; lte?: number | null | undefined };
	recoveries: { gte?: number | null | undefined; lte?: number | null | undefined };
	passes: { gte?: number | null | undefined; lte?: number | null | undefined };
	passesAccuracy: { gte?: number | null | undefined; lte?: number | null | undefined };
	keyPasses: { gte?: number | null | undefined; lte?: number | null | undefined };
	passesForward: { gte?: number | null | undefined; lte?: number | null | undefined };
	crosses: { gte?: number | null | undefined; lte?: number | null | undefined };
	controlUnderPressure: { gte?: number | null | undefined; lte?: number | null | undefined };
	goalsConceded: { gte?: number | null | undefined; lte?: number | null | undefined };
	catches: { gte?: number | null | undefined; lte?: number | null | undefined };
	punches: { gte?: number | null | undefined; lte?: number | null | undefined };
	goalsKicks: { gte?: number | null | undefined; lte?: number | null | undefined };
	aerialClearances: { gte?: number | null | undefined; lte?: number | null | undefined };
};

export type LeagueCountriesData = SelectedCountriesResponse;
export interface ClubsPageFilter {
	search: string;
	order: string;
	countries: string[];
	regions: string[];
	page: number;
	limit: number;
}

export interface MyGamesFilter {
	order: string;
	limit: number;
	page: number;
	search?: string;
	isFavorite?: boolean;
	countryCodes: string[];
	region: string[];
	teamLevel?: string;
	season?: number;
	category?: string[];
	startDate?: string;
	endDate?: string;
}

export interface MyTeamsFilter {
	order: string;
	limit: number;
	page: number;
	search?: string;
	countries?: string[];
	regions?: string[];
	category?: string[];
}

export type Stats = { position: string; percentile: number; value: number }[];

export type Statistics = {
	statistics: Record<string, Stats>;
};

type TermsofUseRole = Role.ROLE_CLUB_MANAGER | Role.ROLE_RECRUITER | Role.ROLE_COACH;
type TermsofUseComponentVersion = TermsOfUseVersion.V1 | TermsOfUseVersion.V2;
type TermsofUseLang = "EN" | "FR";

export type TermKey = `${TermsofUseComponentVersion}${TermsofUseRole}TermsOfUse${TermsofUseLang}`;

export type DatagridColumnMeta = {
	// filterRenderer?: ReturnType<FilterRenderers<any>[keyof FilterRenderers<any>]>;
	tooltip?: string;
};

export enum UserSubscriptionOptions {
	FULL = "full",
	PARTIAL = "partial",
	NONE = "none",
}
